import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Send, Loader2, MoreVertical, X, Star } from "lucide-react";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFamily } from "@/contexts/FamilyContext";
import { useDeepSeekAPI } from "@/hooks/useDeepSeekAPI";
import {
  getSession,
  upsertMessage,
  clearSession,
  type TopicSessionMessage,
} from "@/stores/helpTopicSessions";
import type { HelpChipItem } from "@/data/helpTopicChips";
import { HelpResponseBlocks } from "@/components/help/HelpResponseBlocks";
import { cn } from "@/lib/utils";
import { stripHelpDoctorSection } from "@/utils/stripHelpDoctorSection";
import { scheduleScrollContainerToBottom } from "@/utils/scheduleScrollContainerToBottom";
import {
  applyTextareaAutosize,
  TEXTAREA_AUTOSIZE_DEFAULT_MAX_PX,
} from "@/utils/textareaAutosize";
import { trackPaywallTextShown } from "@/utils/paywallTextAnalytics";

const MAX_MESSAGES = 12;

/** Как во вкладке «Чат»: ниже порога считаем, что пользователь у низа ленты. */
const NEAR_BOTTOM_THRESHOLD_PX = 120;

/** Первые 2–3 абзаца для preview Free-пользователя. */
function truncateToPreview(content: string, maxParagraphs = 3): string {
  const safe = (content ?? "").trim();
  if (!safe) return "";
  const paragraphs = safe.split(/\n\n+/);
  const take = Math.min(maxParagraphs, paragraphs.length);
  return paragraphs.slice(0, take).join("\n\n").trim();
}

export interface TopicConsultationSheetProps {
  isOpen: boolean;
  onClose: () => void;
  topicKey: string;
  topicTitle: string;
  /** Чипсы: label на экране, text вставляется в input по клику (без автоотправки). */
  chips: HelpChipItem[];
  isLocked?: boolean;
  lockedDescription?: string;
  onOpenPremium?: () => void;
  /** Для quick topic: есть доступ (Premium/Trial). Если false, тап по premium-чипу открывает paywall. */
  hasAccess?: boolean;
  /** Для quick topic: вызвать при тапе по premium-чипу у Free пользователя (открыть paywall). */
  onPremiumChipTap?: () => void;
  /** Для quick topic: тексты premium-чипов. Fail-safe: не отправлять такие сообщения Free пользователю. */
  premiumChipTexts?: string[];
  /** При LIMIT_REACHED (help 2/день у Free) — открыть paywall; payload.used обновляет счётчик. */
  onLimitReached?: (payload?: { feature: string; limit: number; used: number }) => void;
  /** Premium/Trial: дневной лимит help исчерпан (ответ 429 PREMIUM_DAILY_LIMIT_REACHED). */
  onPremiumHelpLimit?: (payload?: { feature?: string; limit?: number; used?: number }) => void;
  /** У Premium/Trial при исчерпанном лимите — не вызывать API, сразу мягкий ответ и onPremiumHelpLimit. */
  premiumHelpLimitGate?: { used: number; limit: number; blocked: boolean };
  /** При открытии sheet подставить этот текст в поле ввода (hero чипсы / карточка «Сегодня спрашивают» / тема). Без автоотправки — отправка только по кнопке. */
  initialMessage?: string | null;
  onInitialMessageSent?: () => void;
  /** Текст вопроса «Сегодня спрашивают», если он premium (для preview у Free). */
  popularQuestionTextIfPremium?: string | null;
  /** Free: просмотр статического контента темы; отправка вопроса → paywall. */
  browseContent?: {
    intro: string[];
    checklistNow?: string[];
    redFlags?: string[];
  };
}

export function TopicConsultationSheet({
  isOpen,
  onClose,
  topicKey,
  topicTitle,
  chips,
  isLocked = false,
  lockedDescription,
  onOpenPremium,
  hasAccess = true,
  onPremiumChipTap,
  premiumChipTexts,
  onLimitReached,
  onPremiumHelpLimit,
  premiumHelpLimitGate,
  initialMessage,
  onInitialMessageSent,
  popularQuestionTextIfPremium = null,
  browseContent,
}: TopicConsultationSheetProps) {
  useEffect(() => {
    if (isOpen) {
      trackPaywallTextShown(`topic_consultation_${topicKey}`, { surface: "topic_consultation" });
    }
  }, [isOpen, topicKey]);

  const { selectedMemberId, members } = useFamily();
  const { chat } = useDeepSeekAPI();
  /** "family" = общий ответ / уточнить о ком речь; иначе id ребёнка для персонального ответа. */
  const memberId =
    selectedMemberId === "family" || selectedMemberId == null
      ? "family"
      : selectedMemberId || members[0]?.id || "family";

  const [messages, setMessages] = useState<TopicSessionMessage[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** У низа ленты: после отправки = true; сбрасывается, если ушли вверх дальше NEAR_BOTTOM_THRESHOLD_PX */
  const stickToBottomRef = useRef(false);
  /** Чтобы при сбросе initialMessage родителем не затирать уже подставленный текст */
  const initialPrefillConsumedRef = useRef(false);
  const wasSheetOpenRef = useRef(false);

  const loadSession = useCallback(() => {
    if (!memberId || !topicKey) return;
    const session = getSession(memberId, topicKey);
    setMessages(session ?? []);
  }, [memberId, topicKey]);

  useEffect(() => {
    if (isOpen) {
      if (!wasSheetOpenRef.current) {
        stickToBottomRef.current = false;
      }
      wasSheetOpenRef.current = true;
      loadSession();
      if (initialMessage?.trim()) {
        setInput(initialMessage);
        onInitialMessageSent?.();
        initialPrefillConsumedRef.current = true;
        inputRef.current?.focus();
      } else if (!initialPrefillConsumedRef.current) {
        setInput("");
      }
    } else {
      wasSheetOpenRef.current = false;
      initialPrefillConsumedRef.current = false;
    }
  }, [isOpen, initialMessage, loadSession, onInitialMessageSent]);

  useEffect(() => {
    if (isOpen && memberId && topicKey) loadSession();
  }, [memberId, topicKey, isOpen, loadSession]);

  useEffect(() => {
    stickToBottomRef.current = false;
  }, [topicKey, memberId]);

  const syncComposerHeight = useCallback(() => {
    applyTextareaAutosize(inputRef.current, TEXTAREA_AUTOSIZE_DEFAULT_MAX_PX);
  }, []);

  useLayoutEffect(() => {
    syncComposerHeight();
  }, [input, syncComposerHeight]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = dist <= NEAR_BOTTOM_THRESHOLD_PX;
  }, []);

  // Прокрутка к низу после отправки и при росте ленты (ответ, «Думаю…»), только пока пользователь не ушёл вверх
  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return;
    scheduleScrollContainerToBottom(messagesScrollRef.current);
  }, [messages, isSending]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !topicKey) return;
      if (isLocked) {
        onOpenPremium?.();
        return;
      }

      const userMsg: TopicSessionMessage = {
        id: `u-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      const assistantId = `a-${Date.now()}`;
      const assistantPlaceholder: TopicSessionMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date().toISOString(),
      };

      if (premiumHelpLimitGate?.blocked) {
        const soft =
          "Сегодня вы уже использовали доступное количество запросов. Лимит обновится завтра — спасибо, что пользуетесь приложением.";
        const assistantFinal: TopicSessionMessage = {
          id: assistantId,
          role: "assistant",
          content: soft,
          timestamp: new Date().toISOString(),
        };
        stickToBottomRef.current = true;
        setMessages((prev) => {
          const next = [...prev, userMsg, assistantFinal].slice(-MAX_MESSAGES);
          upsertMessage(memberId, topicKey, userMsg);
          upsertMessage(memberId, topicKey, assistantFinal);
          return next;
        });
        setInput("");
        onPremiumHelpLimit?.({
          feature: "help",
          limit: premiumHelpLimitGate.limit,
          used: premiumHelpLimitGate.used,
        });
        return;
      }

      stickToBottomRef.current = true;

      setMessages((prev) => {
        const next = [...prev, userMsg, assistantPlaceholder].slice(-MAX_MESSAGES);
        upsertMessage(memberId, topicKey, userMsg);
        return next;
      });
      setIsSending(true);
      setInput("");

      try {
        const chatMessages = messages.map((m) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
        }));
        chatMessages.push({ role: "user", content: trimmed });

        const response = await chat({
          messages: chatMessages,
          type: "sos_consultant",
          overrideSelectedMemberId: memberId || undefined,
        });
        const rawMessage = (response?.message ?? "").trim() || "Не удалось получить ответ.";

        const assistantMsg: TopicSessionMessage = {
          id: assistantId,
          role: "assistant",
          content: rawMessage,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => {
          const next = prev.map((m) =>
            m.id === assistantId ? assistantMsg : m
          );
          upsertMessage(memberId, topicKey, assistantMsg);
          return next;
        });
      } catch (err) {
        const msg = (err as { message?: string })?.message;
        const payload = (err as { payload?: { feature: string; limit: number; used: number } })?.payload;
        const premiumPayload = (err as { payload?: { feature?: string; limit?: number; used?: number } })?.payload;
        if (msg === "PREMIUM_DAILY_LIMIT_REACHED") {
          onPremiumHelpLimit?.(premiumPayload);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content:
                      "Сегодня вы уже использовали доступное количество запросов. Лимит обновится завтра — спасибо, что пользуетесь приложением.",
                  }
                : m
            )
          );
          return;
        }
        if (msg === "LIMIT_REACHED" && onLimitReached) {
          onLimitReached(payload);
        }
        const fallbackText =
          msg === "HELP_TIMEOUT"
            ? "Ответ занимает больше времени. Попробуйте ещё раз."
            : msg === "LIMIT_REACHED"
              ? "Лимит на сегодня исчерпан. Попробуйте завтра или откройте полную версию."
              : "Ошибка отправки. Попробуйте ещё раз.";
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: fallbackText } : m))
        );
      } finally {
        setIsSending(false);
      }
    },
    [
      memberId,
      topicKey,
      messages,
      chat,
      isLocked,
      hasAccess,
      premiumChipTexts,
      onPremiumChipTap,
      onLimitReached,
      onPremiumHelpLimit,
      premiumHelpLimitGate,
      onOpenPremium,
    ]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleClearSession = () => {
    clearSession(memberId, topicKey);
    setMessages([]);
  };

  /** Чипс: premium без доступа → paywall; иначе вставить text в input */
  const handleChipClick = (chip: HelpChipItem) => {
    if (isLocked) return;
    if (chip.access === "paid" && !hasAccess && onPremiumChipTap) {
      onPremiumChipTap();
      return;
    }
    setInput(chip.text);
    inputRef.current?.focus();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        overlayClassName="bg-black/20"
        className={cn(
          "h-[90dvh] max-h-[92dvh] rounded-t-2xl border-t flex flex-col p-0 gap-0",
          "[&>button]:hidden"
        )}
      >
        <div className="flex flex-col flex-1 min-h-0">
          {/* Header: только title + close */}
          <div className="flex items-center gap-2 shrink-0 px-4 py-3 border-b border-border">
            <h2
              className="flex-1 min-w-0 text-[17px] font-semibold text-foreground leading-snug line-clamp-2 break-words hyphens-auto"
              style={{ wordBreak: "break-word" }}
            >
              {topicTitle}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Закрыть"
              className="shrink-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {isLocked ? (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {browseContent?.intro.map((p, i) => (
                  <p key={`intro-${i}`} className="text-sm text-muted-foreground leading-[1.6]">
                    {p}
                  </p>
                ))}
                {!browseContent && lockedDescription && (
                  <p className="text-sm text-muted-foreground leading-[1.6]">{lockedDescription}</p>
                )}
                {browseContent?.checklistNow && browseContent.checklistNow.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wide">
                      Что можно сделать сейчас
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-4">
                      {browseContent.checklistNow.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-muted-foreground/90 pt-2">
                  Персональный ответ по этой теме — в полной версии.
                </p>
              </div>
              <div className="shrink-0 px-4 py-3 border-t border-border/80 space-y-2 bg-background">
                <p className="text-[12px] text-muted-foreground text-center">
                  Напишите вопрос — откроем полную версию
                </p>
                <div className="flex gap-2 items-center rounded-full bg-slate-100/80 px-3 py-2 border border-slate-200/80">
                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ваш вопрос…"
                    className={cn(
                      "resize-none flex-1 min-w-0 min-h-[40px] max-h-20 bg-transparent border-0",
                      "text-sm leading-5 py-2 pr-2 pl-0 placeholder:text-muted-foreground",
                      "focus:outline-none"
                    )}
                  />
                  <Button
                    type="button"
                    size="icon"
                    className="shrink-0 h-9 w-9 rounded-full"
                    disabled={!input.trim()}
                    onClick={() => sendMessage(input)}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Menu: clear session */}
              <div className="flex justify-end px-2 py-1 shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Меню">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleClearSession}>
                      Очистить консультацию
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Подсказка над чипсами */}
              <p className="shrink-0 px-4 text-[12px] text-muted-foreground pb-1.5">
                Выберите быстрый вопрос или опишите своими словами
              </p>

              {/* Чип-ряд: Free сначала, затем Premium (с маркером для Free пользователя) */}
              <div className="shrink-0 px-4 pb-2">
                <div className="flex gap-2 overflow-x-auto pb-1 flex-nowrap overflow-y-hidden">
                  {[...chips]
                    .sort((a, b) => (a.access === "free" ? 0 : 1) - (b.access === "free" ? 0 : 1))
                    .map((chip) => {
                      const isPremiumLocked = chip.access === "paid" && !hasAccess;
                      return (
                        <button
                          key={chip.text}
                          type="button"
                          onClick={() => handleChipClick(chip)}
                          disabled={isSending}
                          className={cn(
                            "shrink-0 px-3 py-2 rounded-full text-[13px] font-medium border transition-colors duration-200 whitespace-nowrap flex items-center gap-1",
                            isPremiumLocked
                              ? "border-amber-200 bg-amber-50/80 text-foreground hover:bg-amber-100/80 active:bg-amber-100"
                              : "border-border/80 bg-background text-foreground hover:bg-muted/30 active:bg-primary/5 active:border-primary/20"
                          )}
                        >
                          {isPremiumLocked && (
                            <Star className="w-3 h-3 text-premium-star shrink-0" aria-hidden />
                          )}
                          {chip.label}
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Messages */}
              <div
                ref={messagesScrollRef}
                onScroll={handleMessagesScroll}
                className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-2 min-h-0"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                <div className="space-y-3 pb-4">
                  {messages.map((m, index) => {
                    const isLastAssistant =
                      m.role === "assistant" &&
                      index === messages.length - 1 &&
                      m.content.length > 0;
                    const precedingUserContent = m.role === "assistant" && index > 0
                      ? (messages[index - 1]?.role === "user" ? messages[index - 1].content?.trim() : "")
                      : "";
                    const isPremiumPrompt = Boolean(
                      topicKey === "quick" &&
                      precedingUserContent &&
                      (premiumChipTexts?.some((p) => p.trim() === precedingUserContent) ||
                        precedingUserContent === popularQuestionTextIfPremium?.trim())
                    );
                    const showPreview =
                      isLastAssistant &&
                      !hasAccess &&
                      isPremiumPrompt &&
                      !["Ответ занимает больше времени. Попробуйте ещё раз.", "Ошибка отправки. Попробуйте ещё раз.", "Не удалось получить ответ."].includes((m.content ?? "").trim());
                    const bodyForDisplay = stripHelpDoctorSection(m.content ?? "");
                    const displayContent = showPreview
                      ? truncateToPreview(bodyForDisplay)
                      : bodyForDisplay;

                    return (
                      <div
                        key={m.id}
                        className={cn(
                          "flex",
                          m.role === "user" ? "justify-end" : "justify-start"
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-[1.6]",
                            m.role === "user"
                              ? "bg-primary text-primary-foreground rounded-br-md"
                              : "bg-muted/50 text-foreground rounded-bl-md"
                          )}
                        >
                        {m.role === "user" ? (
                          <p className="break-words whitespace-pre-wrap">{m.content}</p>
                        ) : (
                          <>
                            <HelpResponseBlocks content={displayContent} messageId={m.id} />
                            {showPreview && onPremiumChipTap && (
                              <div className="mt-4 pt-3 border-t border-border/60 space-y-3">
                                <p className="text-[13px] text-muted-foreground leading-snug">
                                  Продолжение ответа — в полной версии, без сокращений.
                                </p>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="rounded-xl font-medium bg-primary text-primary-foreground"
                                  onClick={onPremiumChipTap}
                                >
                                  Получить полный разбор
                                </Button>
                              </div>
                            )}
                            {(m.content === "Ответ занимает больше времени. Попробуйте ещё раз." ||
                              m.content === "Ошибка отправки. Попробуйте ещё раз.") &&
                              index === messages.length - 1 &&
                              messages.length >= 2 &&
                              !showPreview && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-2"
                                  onClick={() => {
                                    const lastUser = messages[messages.length - 2];
                                    if (lastUser?.role === "user") sendMessage(lastUser.content);
                                  }}
                                >
                                  Повторить
                                </Button>
                              )}
                          </>
                        )}
                        </div>
                      </div>
                    );
                  })}
                  {isSending && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl rounded-bl-md px-4 py-2.5 bg-muted/50 flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Думаю…</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Composer Help: тот же единый «капсульный» composer, что ChatInputBar */}
              <form
                onSubmit={handleSubmit}
                className="shrink-0 p-4 pt-2 bg-background"
              >
                <div
                  className={cn(
                    "chat-composer-capsule",
                    "flex w-full min-w-0 max-w-full items-end gap-2",
                    "rounded-2xl overflow-hidden",
                    "bg-muted/50 dark:bg-muted/30",
                    "pl-3 pr-2 py-2",
                    "transition-[border-color] duration-200",
                    "border-2 border-primary/40",
                    "focus-within:border-primary"
                  )}
                >
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onInput={syncComposerHeight}
                    placeholder="Напишите, что происходит…"
                    rows={1}
                    autoComplete="off"
                    className={cn(
                      "chat-composer-field",
                      "flex-1 min-w-0 min-h-[40px] max-h-[120px] w-full resize-none scrollbar-none",
                      "rounded-none border-0 bg-transparent shadow-none",
                      "text-foreground",
                      "!outline-none focus:!outline-none focus-visible:!outline-none",
                      "!shadow-none focus:!shadow-none focus-visible:!shadow-none",
                      "!ring-0 !ring-offset-0 focus-visible:!ring-0 focus-visible:!ring-offset-0",
                      "leading-5 text-sm py-2 pr-2 pl-0 placeholder:text-muted-foreground",
                      "disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                    disabled={isSending}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage(input);
                      }
                    }}
                  />
                  <Button
                    type="submit"
                    className={cn(
                      "h-10 min-w-10 shrink-0 rounded-xl px-3",
                      "bg-primary text-primary-foreground shadow-none",
                      "hover:bg-primary/90",
                      "ring-offset-0 focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-0",
                      "[&_svg]:!size-4"
                    )}
                    disabled={!input.trim() || isSending}
                  >
                    {isSending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
